import { ReviewPlatform } from 'src/services/reviewAnalysisApi/interfaces/reviewAnalysis.interface'

export interface DetectedSource {
  // a link, or a bare Google Place ID
  kind: 'link' | 'placeId'
  // a readable location name taken from the link, '' when none
  name: string
  platform: ReviewPlatform | null
  // the link as it will be stored (https:// added when missing)
  value: string
}

// Google Place IDs start with "ChIJ" (the common form) or "GhIJ" / "EiI".
const PLACE_ID = /^(ChIJ|GhIJ|EiI)[\w-]{10,}$/

const toUrl = (input: string): URL | null => {
  try {
    return new URL(/^https?:\/\//i.test(input) ? input : `https://${input}`)
  } catch {
    return null
  }
}

// A site's own country domains: google.com, google.ge, google.co.uk,
// google.com.au. Anchored, so google.evil.com is not Google.
const siteHost = (site: string) =>
  new RegExp(
    `^(?:[\\w-]+\\.)*${site}\\.(?:com|[a-z]{2}|co\\.[a-z]{2}|com\\.[a-z]{2})$`
  )
const GOOGLE_HOST = siteHost('google')
const GOOGLE_MAPS_HOST = /^maps\.google\./
const TRIPADVISOR_HOST = siteHost('tripadvisor')
const EXPEDIA_HOST = siteHost('expedia')

const platformOf = (url: URL): ReviewPlatform | null => {
  const host = url.hostname.toLowerCase().replace(/^www\./, '')
  const path = url.pathname.toLowerCase()
  if (host === 'play.google.com') return 'playstore'
  if (host === 'apps.apple.com' || host === 'itunes.apple.com')
    return 'appstore'
  if (
    host === 'maps.app.goo.gl' ||
    host === 'g.page' ||
    (host === 'goo.gl' && path.startsWith('/maps')) ||
    (GOOGLE_HOST.test(host) &&
      (GOOGLE_MAPS_HOST.test(host) || path.startsWith('/maps')))
  )
    return 'google'
  if (TRIPADVISOR_HOST.test(host)) return 'tripadvisor'
  if (/(^|\.)booking\.com$/.test(host)) return 'booking'
  if (EXPEDIA_HOST.test(host)) return 'expedia'
  return null
}

const words = (s: string) =>
  decodeURIComponent(s).replace(/[+_]/g, ' ').replace(/\s+/g, ' ').trim()

const titleCase = (s: string) => s.replace(/\b\w/g, (c) => c.toUpperCase())

// Best-effort location name from the link's path; the user can edit it.
const nameFrom = (url: URL, platform: ReviewPlatform): string => {
  const path = url.pathname
  try {
    switch (platform) {
      case 'google': {
        const m = path.match(/\/maps\/place\/([^/]+)/)
        return m ? words(m[1]) : ''
      }
      case 'tripadvisor': {
        // .../Hotel_Review-g294195-d1234-Reviews-Stamba_Hotel-Tbilisi.html
        const m = path.match(/-Reviews-(.+?)(\.html)?$/)
        return m ? words(m[1].replace(/-/g, ' ')) : ''
      }
      case 'booking': {
        // /hotel/ge/stamba-tbilisi.en-gb.html
        const m = path.match(/\/hotel\/[a-z]{2}\/([^./]+)/)
        return m ? titleCase(words(m[1].replace(/-/g, ' '))) : ''
      }
      case 'expedia': {
        // /Tbilisi-Hotels-Stamba-Hotel.h12345.Hotel-Information
        const m = path.match(/\/(?:[^/]*-Hotels-)?([^/]+?)\.h\d+/)
        return m ? words(m[1].replace(/-/g, ' ')) : ''
      }
      case 'appstore': {
        // /us/app/stamba/id123
        const m = path.match(/\/app\/([^/]+)\//)
        return m ? titleCase(words(m[1].replace(/-/g, ' '))) : ''
      }
      case 'playstore':
        return url.searchParams.get('id') ?? ''
      default:
        return ''
    }
  } catch {
    return ''
  }
}

export const detectSource = (raw: string): DetectedSource => {
  const input = raw.trim()
  if (PLACE_ID.test(input)) {
    return { kind: 'placeId', name: '', platform: 'google', value: input }
  }
  const url = toUrl(input)
  const platform = url && url.hostname.includes('.') ? platformOf(url) : null
  return {
    kind: 'link',
    name: url && platform ? nameFrom(url, platform) : '',
    platform,
    value: url ? url.toString() : input,
  }
}

// The same link written two ways (www or not, a trailing slash, the host's
// case) is one source.
export const sourceKey = (value: string): string => {
  const url = /^https?:\/\//i.test(value) ? toUrl(value) : null
  if (!url) return value
  const host = url.hostname.toLowerCase().replace(/^www\./, '')
  return `${host}${url.pathname.replace(/\/+$/, '')}${url.search}`
}

// A comma only separates two cells when a source or a quoted cell starts
// right after it: Google Maps links carry commas of their own
// (/@41.7,44.7,17z). A scheme-less link (booking.com/hotel/...) counts; its
// top-level domain is letters, so a decimal zoom (,16.5z/) is not one.
const CELL_SEPARATOR =
  /,(?=\s*(?:["']|https?:\/\/|www\.|ChIJ|GhIJ|EiI|[\w-]+(?:\.[\w-]+)*\.[A-Za-z]{2,}\/))/

// Where a second source starts inside one cell: after whitespace, or glued
// straight onto the first link (…/AbChttps://…). Not after = % & ?, where a
// link is a parameter of the one before it.
const JOINED_START = /\s+(?=https?:\/\/|www\.|ChIJ|GhIJ|EiI)/
const splitJoined = (cell: string): string[] =>
  cell
    .replace(/([^\s=%&?])(https?:\/\/)/g, '$1 $2')
    .split(JOINED_START)
    .map((part) => part.trim())
    .filter(Boolean)

// Text that cannot be a link or a Place ID: a CSV name or header cell. A name
// may hold a dot ("St. Regis"); a bare domain (booking.com) is not a name.
const isPlainText = (cell: string) =>
  !cell.includes('/') &&
  !/^[\w-]+(?:\.[\w-]+)+$/.test(cell) &&
  !PLACE_ID.test(cell)

const unquote = (cell: string) =>
  cell
    .trim()
    .replace(/^["']|["']$/g, '')
    .trim()

export interface SourceInput {
  // a location name given next to the link (a CSV `name,url` row), '' when none
  name: string
  text: string
}

// Links / Place IDs out of pasted text or an uploaded CSV / TXT: one per line,
// tabs, semicolons, quotes and CSV commas between sources tolerated. In a row
// with one source, a plain-text cell before it is that location's name, and a
// header row of plain text only (`name,url`) is skipped.
export const splitSources = (text: string): SourceInput[] => {
  const seen = new Set<string>()
  const out: SourceInput[] = []
  const lines = text
    .split(/[\n\r;\t]+/)
    .map((line) => line.trim())
    .filter(Boolean)
  lines.forEach((line, index) => {
    const cells = line.split(CELL_SEPARATOR).map(unquote).filter(Boolean)
    // a name cell has no separator lookahead to split it off, so try `name,`
    const first = cells[0] ?? ''
    const comma = first.indexOf(',')
    if (cells.length === 1 && comma > 0 && isPlainText(first.slice(0, comma))) {
      cells.splice(
        0,
        1,
        unquote(first.slice(0, comma)),
        unquote(first.slice(comma + 1))
      )
    }
    // two links pasted on one line, with a space or nothing between them,
    // are two sources (they used to become one location)
    const parts = cells.flatMap(splitJoined)
    const plain = parts.filter((c) => c && isPlainText(c))
    const sources = parts.filter((c) => c.length > 3 && !isPlainText(c))
    if (sources.length === 0) {
      // a header row; anything else plain is kept so it can be fixed
      if (index === 0 && line.includes(',')) return
      plain.filter((c) => c.length > 3).forEach((c) => sources.push(c))
    }
    const name = sources.length === 1 && plain.length === 1 ? plain[0] : ''
    for (const source of sources) {
      if (seen.has(source)) continue
      seen.add(source)
      out.push({ name, text: source })
    }
  })
  return out
}
